import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';

// Composant de barre de recherche avec autocompletion intelligente
// et navigation au clavier
function SearchBar() {
    // States
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState({ startsWith: [], contains: [] });
    const [showSuggestions, setShowSuggestions] = useState(false);
    // Index de la suggestion selectionnee avec le clavier
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // Ref pour detecter les clics en dehors
    const containerRef = useRef(null);

    const navigate = useNavigate();

    // Fonction pour filtrer les suggestions en deux groupes
    // Groupe 1: Recettes qui COMMENCENT par la recherche
    // Groupe 2: Recettes qui CONTIENNENT la recherche (mais ne commencent pas par)
    const filterSuggestions = (meals, query) => {
        if (!meals) return { startsWith: [], contains: [] };

        const lowerQuery = query.toLowerCase();

        // Recettes qui commencent par la recherche
        const startsWithResults = meals.filter((meal) =>
            meal.strMeal.toLowerCase().startsWith(lowerQuery)
        );

        // Recettes qui contiennent la recherche mais ne commencent pas par
        const containsResults = meals.filter((meal) => {
            const lowerName = meal.strMeal.toLowerCase();
            return lowerName.includes(lowerQuery) && !lowerName.startsWith(lowerQuery);
        });

        return {
            startsWith: startsWithResults.slice(0, 5),
            contains: containsResults.slice(0, 5)
        };
    };

    // useEffect avec debounce pour appeler l'API
    // Le debounce attend 300ms apres la derniere frappe avant d'appeler l'API
    useEffect(() => {
        // Fonction pour recuperer les suggestions depuis l'API
        const fetchSuggestions = async (query) => {
            try {
                const response = await fetch(
                    `https://www.themealdb.com/api/json/v1/1/search.php?s=${query}`
                );
                const data = await response.json();

                const filtered = filterSuggestions(data.meals, query);
                setSuggestions(filtered);
                // Reset de l'index quand les suggestions changent
                setSelectedIndex(-1);
            } catch (error) {
                console.error('Erreur:', error);
                setSuggestions({ startsWith: [], contains: [] });
            }
        };

        // Timer pour le debounce
        const timer = setTimeout(() => {
            // On appelle l'API seulement si au moins 2 caracteres
            if (searchTerm.length >= 2) {
                fetchSuggestions(searchTerm);
            } else {
                setSuggestions({ startsWith: [], contains: [] });
            }
        }, 300);

        // Cleanup: on annule le timer si l'utilisateur tape avant 300ms
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // useEffect pour detecter les clics en dehors du composant
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Combine toutes les suggestions en un seul tableau pour la navigation
    const getAllSuggestions = () => {
        return [...suggestions.startsWith, ...suggestions.contains];
    };

    // Gestion des touches clavier
    const handleKeyDown = (e) => {
        const allSuggestions = getAllSuggestions();

        if (e.key === 'ArrowDown') {
            // Fleche vers le bas: aller a la suggestion suivante
            e.preventDefault();
            setSelectedIndex((prev) =>
                Math.min(prev + 1, allSuggestions.length - 1)
            );
        } else if (e.key === 'ArrowUp') {
            // Fleche vers le haut: aller a la suggestion precedente
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            // Entree: selectionner la suggestion active
            e.preventDefault();
            handleSuggestionClick(allSuggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
            // Echap: fermer les suggestions
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    // Soumission du formulaire
    const handleSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            setShowSuggestions(false);
            navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
        }
    };

    // Clic sur une suggestion
    const handleSuggestionClick = (meal) => {
        setSearchTerm(meal.strMeal);
        setShowSuggestions(false);
        setSelectedIndex(-1);
        navigate(`/search?q=${encodeURIComponent(meal.strMeal)}`);
    };

    // Verifie si une suggestion est selectionnee
    const isSelected = (meal) => {
        const allSuggestions = getAllSuggestions();
        const index = allSuggestions.findIndex((m) => m.idMeal === meal.idMeal);
        return index === selectedIndex;
    };

    const hasSuggestions = suggestions.startsWith.length > 0 || suggestions.contains.length > 0;

    return (
        <div className="search-bar-container" ref={containerRef}>
            <form className="search-bar" onSubmit={handleSubmit}>
                <input
                    type="text"
                    className="search-input"
                    placeholder="Rechercher une recette..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                />
                <button type="submit" className="search-button">
                    Rechercher
                </button>
            </form>

            {/* Liste des suggestions */}
            {showSuggestions && hasSuggestions && (
                <div className="suggestions-list">
                    {/* Groupe 1: Recettes qui commencent par */}
                    {suggestions.startsWith.length > 0 && (
                        <div className="suggestions-group">
                            <div className="suggestions-label">Commencent par "{searchTerm}"</div>
                            {suggestions.startsWith.map((meal) => (
                                <div
                                    key={meal.idMeal}
                                    className={`suggestion-item ${isSelected(meal) ? 'selected' : ''}`}
                                    onClick={() => handleSuggestionClick(meal)}
                                >
                                    <img
                                        src={meal.strMealThumb}
                                        alt={meal.strMeal}
                                        className="suggestion-image"
                                    />
                                    <span className="suggestion-name">{meal.strMeal}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Separateur */}
                    {suggestions.startsWith.length > 0 && suggestions.contains.length > 0 && (
                        <div className="suggestions-separator"></div>
                    )}

                    {/* Groupe 2: Recettes qui contiennent */}
                    {suggestions.contains.length > 0 && (
                        <div className="suggestions-group">
                            <div className="suggestions-label">Contiennent "{searchTerm}"</div>
                            {suggestions.contains.map((meal) => (
                                <div
                                    key={meal.idMeal}
                                    className={`suggestion-item ${isSelected(meal) ? 'selected' : ''}`}
                                    onClick={() => handleSuggestionClick(meal)}
                                >
                                    <img
                                        src={meal.strMealThumb}
                                        alt={meal.strMeal}
                                        className="suggestion-image"
                                    />
                                    <span className="suggestion-name">{meal.strMeal}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default SearchBar;
